/**
 * @file PatientLandingViewModel.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import { Subject } from "rxjs";
import { ApiError } from "../../../../services/models/ApiError";
import ViewModel from "../../../base/ViewModel";
import { GetPatientResponse } from "../../../../services/models/GetPatientResponse";
import { ViewQrResponse } from "../../../../services/models/ViewQrResponse";
import { GenericResponse } from "../../../../services/models/GenericResponse";

/**
 * A class definition for PatientLandingViewModel
 */
export class PatientLandingViewModel extends ViewModel {

    readonly UserType = 'Anonymous'
    
    /**
     * rxjs Variables for ViewQr API
     */
    onLoadQrCode = new Subject<ViewQrResponse>();
    onLoadQrCodeError = new Subject<ApiError>();

    /**
     * rxjs variables for Patient delete API
     */
    onPatientDeleted = new Subject<GenericResponse>();
    onPatientDeletedError = new Subject<ApiError>();

    /**
     * rxjs variables for Getting patient API
     */
    onLoadedPatientList = new Subject<GetPatientResponse>();
    onError = new Subject<ApiError>();

    constructor() {
        super();
    }

    /**
     * Gets the list of the patients
     * @param clinicId string
     * @param accessToken string
     */
    async getPatientList(clinicId: string, accessToken: string, page: number) {
            try {
            const patientLists = await this.apiService.getPatientList(clinicId, accessToken, page);
            this.onLoadedPatientList.next(patientLists);
            } catch(error: unknown){
                this.onError.next((error as any) as ApiError);
            }
    }

    /**
     * Refreshes QR code
     * @param patientId string
     * @param accessToken string
     */

    async refreshQr(patientId: string, accessToken: string) {
        try {
            const viewQrRespone = await this.apiService.viewQR(patientId, accessToken);
            this.onLoadQrCode.next(viewQrRespone as ViewQrResponse)
        } catch (error: unknown) {
            this.onLoadQrCodeError.next(error as ApiError)
        }
    }

    /**
     * Deletes patient
     * @param patientId string
     * @param accessToken string
     */
    async deletePatient(patientId: string, accessToken: string) {
        try {
            const response = await this.apiService.deletePatient(patientId, accessToken);
            this.onPatientDeleted.next(response as GenericResponse)
        } catch (error: unknown) {
            this.onPatientDeletedError.next(error as ApiError)
        }
    }

}